

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { safeGetItem, safeSetItem } from '../../../../lib/utils/localStorage';
import { MapContext as MapContextType, MapProvider } from './types';

const MapContext = createContext<MapContextType | undefined>(undefined);

interface MapProviderContextProps {
  children: ReactNode;
  defaultProvider?: MapProvider;
}

export function MapProviderContext({
  children,
  defaultProvider = 'leaflet'
}: MapProviderContextProps) {
  const [provider, setProvider] = useState<MapProvider>(defaultProvider);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load provider preference from localStorage if available
    const savedProvider = safeGetItem('map-provider') as MapProvider;
    if (savedProvider && (savedProvider === 'google' || savedProvider === 'leaflet')) {
      setProvider(savedProvider);
    }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    // Save provider preference to localStorage
    if (isLoaded) {
      safeSetItem('map-provider', provider);
    }
  }, [provider, isLoaded]);

  const value: MapContextType = {
    provider,
    setProvider,
    isLoaded
  };

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  );
}

export function useMapProvider(): MapContextType {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMapProvider must be used within a MapProviderContext');
  }
  return context;
}

// Hook to check if a specific provider is available
export function useMapProviderAvailability() {
  const [googleMapsAvailable, setGoogleMapsAvailable] = useState(false);
  const [leafletAvailable, setLeafletAvailable] = useState(true); // Leaflet is always available

  useEffect(() => {
    // Check Google Maps availability
    const googleApiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY as string;
    console.log('Google Maps API Key check:', { googleApiKey: googleApiKey ? 'Present' : 'Missing' });
    setGoogleMapsAvailable(!!googleApiKey);
  }, []);

  return {
    google: googleMapsAvailable,
    leaflet: leafletAvailable
  };
}
